/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Materials/MaterialOverview/AdminMaterialOverview.tsx.
 */

import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import "./AdminMaterialOverview.css";
import axiosClient from "../../../../../api/axiosClient";
import DataTable, { Column, FilterConfig } from "../../../../../components/common/DataTable";
import AdminStartReviewModal from "../AdminStartReviewModal";
import {
  CementBoxPlotChart,
  StageBarChart,
  type CementBoxPlotMetric,
  type CementBoxPlotSlotSelection,
} from "../../../../../components";
import { API_URLS } from "../../../../../constants/apiUrls";
import {
  ADMIN_MATERIAL_OVERVIEW_REVIEW_STATUS_FILTER_OPTIONS,
  canAdminApproveDataset,
  formatReviewStatusLabel,
  normalizeReviewStatus,
  REVIEW_STATUS,
  reviewStatusBadgeSuffix,
  sortAdminReviewStatusFilterOptions,
} from "../../../../../constants/reviewStatus";
import { adminApproveSubmission } from "../../../../../services/submissionService";
import {
  extractA1A3EmbodiedCarbon,
  splitChartSlotCodesByReviewQueue,
  type ExpertDataset,
} from "../../../../../services/expertDatasetService";
import {
  buildExpertFlaggedStageBarChartData,
  computeStageBarChartYMax,
  getStageBarChartIndividualLegendLabel,
} from "../../../../../utils/expertFlaggedStageBarChart";
import ExpertChartSlotBanner from "../../../Expert/components/ExpertChartSlotBanner";
import { CheckCircle, Download, Eye, Flag } from "lucide-react";
import AdminDownloadDatasetModal from "./AdminDownloadDatasetModal";

const fmtNum = (v: number | null | undefined): string =>
  v != null ? v.toFixed(3) : "-";

interface SubmissionRow {
  id: string;
  submissionCode: string;
  contributor: {
    id: string;
    name: string;
    email: string;
  } | null;
  background: {
    materialCode: string;
    materialId?: number;
  } | null;
  /** Submission record created in CEPT (not the contributor “submit dataset” moment). */
  createdAt: string;
  submittedDate?: string | null;
  reviewStatus: string;
  currentStage?: string;
  embodiedCarbon?: number | null;
  embodiedCarbonUnit?: string | null;
}

interface Props {
  materialId: number;
  onOpenSubmission: (submissionId: string) => void;
}

const AdminMaterialOverview: React.FC<Props> = ({ materialId, onOpenSubmission }) => {
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [materialName, setMaterialName] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [datasetsError, setDatasetsError] = useState<string | null>(null);
  const [datasets, setDatasets] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    reviewStatus: "",
  });
  const [flagDatasetModalOpen, setFlagDatasetModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionRow | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [chartSlotSelection, setChartSlotSelection] = useState<CementBoxPlotSlotSelection | null>(
    null
  );
  const [metric, setMetric] = useState<CementBoxPlotMetric>("carbon");
  const [selectedStageChartSubmissionId, setSelectedStageChartSubmissionId] =
    useState<string | null>(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const stageChartRef = useRef<HTMLDivElement | null>(null);

  const mapDatasetToRow = (item: any): SubmissionRow => {
    const id = String(item?.id ?? "");
    const submissionCode = String(item?.submissionCode ?? item?.submission_code ?? id);

    const contributorRaw = item?.contributor ?? item?.contributorProfile ?? item?.contributor_profile ?? null;
    const contributor =
      contributorRaw
        ? {
            id: String(contributorRaw?.id ?? contributorRaw?.userId ?? contributorRaw?.user_id ?? ""),
            name: String(contributorRaw?.name ?? contributorRaw?.fullName ?? contributorRaw?.full_name ?? "-"),
            email: String(contributorRaw?.email ?? contributorRaw?.userEmail ?? contributorRaw?.user_email ?? ""),
          }
        : null;

    const bg = item?.background ?? null;
    const materialCode =
      bg?.materialCode ??
      bg?.material_code ??
      bg?.material?.code ??
      item?.materialCode ??
      item?.material_code ??
      "-";

    const createdRaw = item?.createdAt ?? item?.created_at ?? null;
    const createdAt =
      createdRaw != null && createdRaw !== "" ? String(createdRaw) : "";
    const submittedRaw = item?.submittedDate ?? item?.submitted_date ?? null;
    const submittedDate =
      submittedRaw != null && submittedRaw !== ""
        ? String(submittedRaw)
        : null;
    const reviewStatus = String(item?.reviewStatus ?? item?.review_status ?? "PENDING");
    const currentStage = item?.currentStage ?? item?.current_stage ?? undefined;
    const a1a3Summary = item?.a1a3Summary ?? item?.a1a3_summary ?? null;
    const { embodiedCarbon, embodiedCarbonUnit } = extractA1A3EmbodiedCarbon(a1a3Summary);

    return {
      id,
      submissionCode,
      contributor,
      background: bg
        ? {
            materialCode: String(materialCode),
            materialId: bg?.materialId ?? bg?.material_id ?? bg?.material?.id ?? undefined,
          }
        : { materialCode: String(materialCode) },
      createdAt,
      submittedDate,
      reviewStatus,
      currentStage,
      embodiedCarbon,
      embodiedCarbonUnit,
    };
  };

  const load = useCallback(async () => {
    if (!materialId) return;

    try {
      setLoading(true);
      setLoadingDatasets(true);
      setDatasetsError(null);

      // 1️⃣ Get Material Name
      const m = await axiosClient.get(API_URLS.MATERIALS.BY_ID(materialId));
      setMaterialName(m.data?.data?.name || "");

      // 2️⃣ Get datasets for this material (same API used for the graph)
      const ds = await axiosClient.get(API_URLS.ADMIN.DATASETS, {
        params: { type: "byMaterial", materialId },
      });

      if (!ds.data?.success) {
        setDatasets([]);
        setRows([]);
        setDatasetsError("Failed to load datasets.");
        return;
      }

      const list = ds.data?.byMaterial?.data ?? [];
      const safeList = Array.isArray(list) ? list : [];
      setDatasets(safeList);

      const normalized: SubmissionRow[] = safeList.map(mapDatasetToRow).filter((r) => !!r.id);
      setRows(normalized);
    } catch (err) {
      console.error("Material Overview Load Failed:", err);
      setDatasets([]);
      setRows([]);
      setDatasetsError((err as any)?.response?.data?.message || "Failed to load datasets.");
    } finally {
      setLoading(false);
      setLoadingDatasets(false);
    }
  }, [materialId]);

  // 🔥 Load Material Name + Submissions
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setChartSlotSelection(null);
    setSelectedStageChartSubmissionId(null);
  }, [materialId]);

  useEffect(() => {
    setSelectedStageChartSubmissionId(null);
  }, [chartSlotSelection]);

  useEffect(() => {
    if (!selectedStageChartSubmissionId) return;
    const frameId = window.requestAnimationFrame(() => {
      stageChartRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [selectedStageChartSubmissionId]);

  // Get unique review statuses for filter
  const reviewStatuses = useMemo(() => {
    const statuses = new Set<string>();
    rows.forEach((row: SubmissionRow) => {
      if (row.reviewStatus) {
        statuses.add(row.reviewStatus);
      }
    });
    return Array.from(statuses).sort();
  }, [rows]);

  // Column definitions - same as AdminSubmissionList
  const columns: Column<SubmissionRow>[] = [
    {
      key: "submissionCode",
      label: "Submission Code",
      width: "18%",
    },
    {
      key: "contributor",
      label: "Contributor",
      render: (item) => (
        <div>
          <div style={{ fontWeight: 600, color: "#2d2730" }}>
            {item.contributor?.name || "-"}
          </div>
          {item.contributor?.email && (
            <div style={{ fontSize: "0.85rem", color: "#6b6b6b", marginTop: "2px" }}>
              {item.contributor.email}
            </div>
          )}
        </div>
      ),
      width: "23%",
    },
    {
      key: "background",
      label: "Material",
      render: (item) => (
        <span style={{ fontWeight: 500 }}>
          {item.background?.materialCode || "-"}
        </span>
      ),
      width: "14%",
    },
    {
      key: "embodiedCarbon",
      label: "Embodied Carbon",
      align: "right",
      render: (item) => fmtNum(item.embodiedCarbon),
      width: "10%",
    },
    {
      key: "embodiedCarbonUnit",
      label: "Embodied Carbon Unit",
      render: (item) => item.embodiedCarbonUnit || "-",
      width: "12%",
    },
    {
      key: "reviewStatus",
      label: "Review Status",
      align: "center",
      render: (item) => {
        const status = normalizeReviewStatus(item.reviewStatus);
        const statusClass = `status-badge status-badge-${reviewStatusBadgeSuffix(status)}`;
        return (
          <span className={`status-badge-cell ${statusClass}`}>
            {formatReviewStatusLabel(status)}
          </span>
        );
      },
      width: "22%",
      minWidth: "200px",
    },
    {
      key: "submittedDate",
      label: "Submitted date",
      render: (item) => {
        const raw = item.submittedDate;
        if (raw == null || raw === "") return <span>—</span>;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return <span>—</span>;
        return (
          <div style={{ whiteSpace: "nowrap" }}>
            {d.toLocaleDateString()}{" "}
            {d.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        );
      },
      width: "190px",
    },
    {
      key: "createdAt",
      label: "Created",
      render: (item) => {
        const raw = item.createdAt;
        if (raw == null || raw === "") return <span>—</span>;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return <span>—</span>;
        return (
          <div style={{ whiteSpace: "nowrap" }}>
            {d.toLocaleDateString()}{" "}
            {d.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        );
      },
      width: "190px",
    },
  ];

  // Filter configurations
  const filters: FilterConfig[] = useMemo(() => {
    // Base options match review_status_v2; extra values come from live API rows
    const options = [...ADMIN_MATERIAL_OVERVIEW_REVIEW_STATUS_FILTER_OPTIONS];

    const extra = reviewStatuses
      .map((s) => normalizeReviewStatus(String(s || "")))
      .filter((s) => !!s)
      .filter((s) => s !== REVIEW_STATUS.FLAGGED_BY_EXPERT)
      .filter((s) => !options.some((o) => o.value === s))
      .map((s) => ({
        value: s,
        label: formatReviewStatusLabel(s),
      }));

    return [
      {
        key: "reviewStatus",
        label: "Review Status",
        options: sortAdminReviewStatusFilterOptions([...options, ...extra]),
        defaultValue: "",
      },
    ];
  }, [reviewStatuses]);

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  // Filter submissions based on search and filters
  const filteredRows = useMemo(() => {
    let result = [...rows];

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter((row) => {
        const submissionCode = row.submissionCode?.toLowerCase() || "";
        const contributorName = row.contributor?.name?.toLowerCase() || "";
        const contributorEmail = row.contributor?.email?.toLowerCase() || "";
        const materialCode = row.background?.materialCode?.toLowerCase() || "";
        return submissionCode.includes(searchLower) ||
               contributorName.includes(searchLower) ||
               contributorEmail.includes(searchLower) ||
               materialCode.includes(searchLower);
      });
    }

    // Apply review status filter
    if (filterValues.reviewStatus) {
      const want = filterValues.reviewStatus.toUpperCase();
      result = result.filter((row) => normalizeReviewStatus(row.reviewStatus).toUpperCase() === want);
    }

    return result;
  }, [rows, searchTerm, filterValues]);

  const handleChartSlotClick = useCallback((detail: CementBoxPlotSlotSelection) => {
    if (!detail.datasetIds.length) return;
    setChartSlotSelection((prev) => {
      if (
        prev &&
        prev.slotKey === detail.slotKey &&
        prev.metric === detail.metric
      ) {
        return null;
      }
      return detail;
    });
  }, []);

  const clearChartSlotSelection = useCallback(() => setChartSlotSelection(null), []);

  const nonApprovedDatasetIds = useMemo(
    () =>
      rows
        .filter((row) => normalizeReviewStatus(row.reviewStatus) !== REVIEW_STATUS.APPROVED)
        .map((row) => row.id),
    [rows]
  );

  const chartSlotCodeGroups = useMemo(() => {
    if (!chartSlotSelection) return null;
    return splitChartSlotCodesByReviewQueue(chartSlotSelection, nonApprovedDatasetIds);
  }, [chartSlotSelection, nonApprovedDatasetIds]);

  const selectedStageChartSubmissionCode = useMemo(() => {
    if (!selectedStageChartSubmissionId || !chartSlotCodeGroups) return null;
    const match = chartSlotCodeGroups.allEntries.find(
      (entry) => entry.id === selectedStageChartSubmissionId
    );
    return match?.code ?? selectedStageChartSubmissionId;
  }, [selectedStageChartSubmissionId, chartSlotCodeGroups]);

  const chartDatasets = useMemo(
    () => (datasets ?? []) as ExpertDataset[],
    [datasets]
  );

  const stageBarChartColumnDatasets = useMemo(() => {
    if (!chartSlotSelection) return [];
    const idSet = new Set(chartSlotSelection.datasetIds.map(String));
    return chartDatasets.filter((dataset) => idSet.has(String(dataset.id)));
  }, [chartSlotSelection, chartDatasets]);

  const stageBarChartAverageData = useMemo(
    () =>
      buildExpertFlaggedStageBarChartData({
        allDatasets: chartDatasets,
        metric,
        selectedDatasetId: null,
      }),
    [chartDatasets, metric]
  );

  const stageBarChartData = useMemo(
    () =>
      buildExpertFlaggedStageBarChartData({
        allDatasets: chartDatasets,
        metric,
        selectedDatasetId: selectedStageChartSubmissionId,
      }),
    [chartDatasets, metric, selectedStageChartSubmissionId]
  );

  const stageBarChartYMax = useMemo(
    () =>
      computeStageBarChartYMax({
        chartPoints: stageBarChartAverageData,
        columnDatasets: stageBarChartColumnDatasets,
        metric,
      }),
    [stageBarChartAverageData, stageBarChartColumnDatasets, metric]
  );

  const stageBarIndividualLegend = useMemo(
    () => getStageBarChartIndividualLegendLabel(selectedStageChartSubmissionCode),
    [selectedStageChartSubmissionCode]
  );

  const rowsFilteredByChart = useMemo(() => {
    if (!chartSlotSelection?.datasetIds.length) return filteredRows;
    const idSet = new Set(chartSlotSelection.datasetIds.map(String));
    return filteredRows.filter((r) => idSet.has(String(r.id)));
  }, [filteredRows, chartSlotSelection]);

  const handleFlagDataset = (item: SubmissionRow, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedSubmission(item);
    setFlagDatasetModalOpen(true);
  };

  const handleApproveDataset = async (item: SubmissionRow, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canAdminApproveDataset(item.reviewStatus)) return;
    if (
      !window.confirm(
        `Approve dataset ${item.submissionCode ?? item.id}? This marks the submission as approved.`
      )
    ) {
      return;
    }
    setApprovingId(item.id);
    try {
      const res = await adminApproveSubmission(item.id);
      if (res.data?.success === false) {
        alert(res.data?.message || "Approval failed.");
        return;
      }
      void load();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Approval failed. Please try again.";
      alert(msg);
    } finally {
      setApprovingId(null);
    }
  };

  // Render actions for each row
  const renderActions = (item: SubmissionRow) => {
    const canApprove = canAdminApproveDataset(item.reviewStatus);
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          gap: "8px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <button
          className="view-btn icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            onOpenSubmission(item.id);
          }}
        >
          <Eye size={16} style={{ stroke: "currentColor" }} />
          <span className="btn-text">View</span>
        </button>

        <button
          className="view-btn icon-btn"
          onClick={(e) => handleFlagDataset(item, e)}
          style={{ backgroundColor: "#7a1b49", color: "white" }}
        >
          <Flag size={16} style={{ stroke: "currentColor" }} />
          <span className="btn-text">Flag Dataset</span>
        </button>

        <button
          className="view-btn icon-btn"
          type="button"
          disabled={!canApprove || approvingId === item.id}
          title={
            canApprove
              ? undefined
              : "Cannot approve: already approved or returned to contributor for revision."
          }
          onClick={(e) => handleApproveDataset(item, e)}
          style={{
            backgroundColor: "#2e7d32",
            color: "white",
            opacity: canApprove ? 1 : 0.45,
            cursor: canApprove ? "pointer" : "not-allowed",
          }}
        >
          <CheckCircle size={16} style={{ stroke: "currentColor" }} />
          <span className="btn-text">{approvingId === item.id ? "Approving…" : "Approve"}</span>
        </button>
      </div>
    );
  };

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Material Overview: {materialName || `Material #${materialId}`}</span>
      </div>
      <div className="material-overview-container">
      {(loadingDatasets || !!datasetsError || (datasets?.length ?? 0) > 0) && (
        <div className="material-overview-graphs">
          <div className="graph-card data-visualisation-wrapper">
            {loadingDatasets ? (
              <div className="material-overview-graph-loading">
                <div className="loading-spinner"></div>
                <p>Loading graph...</p>
              </div>
            ) : datasetsError ? (
              <p className="material-overview-graph-hint" style={{ color: "#d32f2f" }}>
                {datasetsError}
              </p>
            ) : (
              <>
                <h2 className="material-overview-graph-title">
                  Comparison of embodied impacts across categorical tiers
                </h2>
                <p className="material-overview-graph-hint material-overview-graph-hint--clickable">
                  Click any chart column to filter submissions in that group (same
                  sub-type, technology, time bucket, and metric as the chart).
                  Then click a submission code in the banner below to open the
                  stage comparison chart. Click the column again to clear.
                </p>
                <CementBoxPlotChart
                  datasets={datasets}
                  showFilters={(datasets?.length ?? 0) > 0}
                  parentMaterialName={materialName}
                  interactiveSlotSelection
                  onSlotClick={handleChartSlotClick}
                  onInteractiveMetricChange={clearChartSlotSelection}
                  metric={metric}
                  onMetricChange={setMetric}
                />
              </>
            )}
          </div>
        </div>
      )}

      {chartSlotSelection ? (
        <ExpertChartSlotBanner
          chartSlotSelection={chartSlotSelection}
          visibleCount={rowsFilteredByChart.length}
          visibleSubmissionCodes={rowsFilteredByChart.map(
            (row) => row.submissionCode || row.id
          )}
          codeGroups={chartSlotCodeGroups}
          selectedSubmissionId={selectedStageChartSubmissionId}
          onSelectSubmissionId={setSelectedStageChartSubmissionId}
          onClear={clearChartSlotSelection}
        />
      ) : null}

      {selectedStageChartSubmissionId ? (
        <div
          ref={stageChartRef}
          className="material-overview-graphs material-overview-stage-chart"
        >
          <div className="graph-card stage-chart">
            <StageBarChart
              metric={metric}
              data={stageBarChartData}
              individualLegendLabel={stageBarIndividualLegend}
              yMax={stageBarChartYMax}
            />
          </div>
        </div>
      ) : null}

      {/* Search + filters directly above the table */}
      <div className="material-overview-header">
        <div className="material-overview-left">
          <div className="data-table-search">
            <svg
              className="search-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14L11.1 11.1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by submission code, contributor name, or email..."
              className="data-table-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {filters.length > 0 && (
          <div className="material-overview-right">
            <div className="data-table-filters">
              {filters.map((filter) => (
                <select
                  key={filter.key}
                  className="data-table-filter-select"
                  value={filterValues[filter.key] || ""}
                  onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                >
                  <option value="">All {filter.label}</option>
                  {filter.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ))}
            </div>
          </div>
        )}
      </div>

      <DataTable<SubmissionRow>
        data={rowsFilteredByChart}
        columns={columns}
        loading={loading}
        pagination={true}
        itemsPerPage={10}
        renderActions={renderActions}
        onRowClick={(item) => onOpenSubmission(item.id)}
        emptyMessage={
          chartSlotSelection &&
          rowsFilteredByChart.length === 0 &&
          filteredRows.length > 0
            ? "No submissions in this chart column match your current search or review-status filter. Clear the chart filter or adjust filters above."
            : `No submissions found for ${materialName || "this material"}`
        }
        renderEmpty={() => (
          <div className="empty-state" role="status" aria-live="polite">
            <div className="empty-icon" aria-hidden="true">
              <svg width="60" height="60" viewBox="0 0 60 60" focusable="false">
                <g fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="16" width="42" height="30" rx="6" ry="6" />
                  <path d="M9 24h42" />
                  <circle cx="20" cy="38" r="3" />
                  <path d="M27 40h18" />
                </g>
              </svg>
            </div>
            <h3 className="empty-title">No submissions yet</h3>
            <p className="empty-desc">
              Once contributors add datasets for <strong>{materialName || "this material"}</strong>,
              they will appear here for your review.
            </p>
            <button type="button" className="empty-action" onClick={() => void load()}>
              Refresh list
            </button>
          </div>
        )}
      />
      {!loading && rows.length > 0 ? (
        <div className="material-overview-download-section">
          <button
            type="button"
            className="material-overview-download-btn"
            onClick={() => setDownloadModalOpen(true)}
          >
            <Download size={18} aria-hidden="true" />
            Download dataset
          </button>
          <p className="material-overview-download-hint">
            Export all submissions for this material as an Excel file (admin flat template).
          </p>
        </div>
      ) : null}
      </div>

      <AdminDownloadDatasetModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        materialId={materialId}
        materialName={materialName}
        submissionCount={rows.length}
      />

      {/* Flag Dataset Modal */}
      {selectedSubmission && (
        <AdminStartReviewModal
          open={flagDatasetModalOpen}
          onClose={() => {
            setFlagDatasetModalOpen(false);
            setSelectedSubmission(null);
          }}
          submissionId={selectedSubmission.id}
          submissionCode={selectedSubmission.submissionCode ?? selectedSubmission.id}
          materialId={selectedSubmission.background?.materialId ?? materialId}
          onReviewStarted={() => {
            void load();
            setFlagDatasetModalOpen(false);
            setSelectedSubmission(null);
          }}
        />
      )}
    </div>
  );
};

/** React component — Admin Material Overview. */
export default AdminMaterialOverview;